import { useState } from "react";
import { Card, CardHeader, CardTitle, CardDescription, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Search } from "lucide-react";
import { NutritionHeader } from "./components/NutritionHeader";
import { useAuth } from "@/components/AuthProvider";
import { useQuery } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import { format } from "date-fns";
import { ptBR, enUS } from "date-fns/locale";
import { Skeleton } from "@/components/ui/skeleton";
import { useTranslations } from "@/hooks/useTranslations";
import { useLanguage } from "@/components/LanguageContext";

export const StudentNutritionList = () => {
  const { user } = useAuth();
  const [searchTerm, setSearchTerm] = useState("");
  const { t } = useTranslations("nutrition");
  const { language } = useLanguage();
  const dateLocale = language === 'pt' ? ptBR : enUS;
  
  const { data: nutritionPlans, isLoading } = useQuery({
    queryKey: ["student-nutrition", user?.email],
    queryFn: async () => {
      if (!user?.email) return [];

      // First get the student ID
      const { data: student, error: studentError } = await supabase
        .from("students")
        .select("id")
        .eq("email", user.email)
        .single();
        
      if (studentError) {
        console.error("Error fetching student:", studentError);
        return [];
      }

      // Then get the nutrition plans
      const { data, error } = await supabase
        .from("nutrition_recommendations")
        .select(`
          *,
          trainer:profiles(first_name, last_name)
        `)
        .eq("student_id", student.id)
        .order("created_at", { ascending: false });
        
      if (error) {
        console.error("Error fetching nutrition plans:", error);
        return [];
      }
      
      return data;
    },
    enabled: !!user?.email
  });

  const filteredPlans = nutritionPlans?.filter(plan => {
    if (!searchTerm) return true;
    
    const searchLower = searchTerm.toLowerCase();
    const date = format(new Date(plan.created_at), "dd/MM/yyyy", { locale: dateLocale });
    
    return date.includes(searchLower) || 
      (plan.notes && plan.notes.toLowerCase().includes(searchLower)) || 
      (plan.activity_level && plan.activity_level.toLowerCase().includes(searchLower));
  });

  return (
    <div className="space-y-6">
      <NutritionHeader />

      <div className="relative flex-1 w-full sm:max-w-xs">
        <Search className="absolute left-2 top-2.5 h-4 w-4 text-muted-foreground" />
        <Input 
          placeholder={t("search.placeholder")} 
          value={searchTerm} 
          onChange={e => setSearchTerm(e.target.value)} 
          className="pl-8 bg-white/90 dark:bg-gray-800/90 border border-slate-100 dark:border-gray-700 rounded-xl shadow-sm focus:ring-1 focus:ring-flame/20" 
        />
      </div>

      {isLoading ? (
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 dark:border-gray-700/50 p-6">
          <div className="animate-pulse space-y-4">
            <Skeleton className="h-4 w-1/4" />
            <Skeleton className="h-10 w-full" />
            <Skeleton className="h-10 w-full" />
          </div>
        </div>
      ) : filteredPlans && filteredPlans.length > 0 ? (
        <div className="space-y-4">
          {filteredPlans.map(plan => (
            <div key={plan.id} className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 dark:border-gray-700/50 overflow-hidden hover:shadow-lg transition-all">
              <Card className="border-0 shadow-none bg-transparent">
                <CardHeader>
                  <CardTitle className="text-foreground">{t("form.newRecommendation")}</CardTitle>
                  <CardDescription className="text-muted-foreground dark:text-gray-400">
                    {language === 'pt' ? 'Criado em ' : 'Created on '} 
                    {format(new Date(plan.created_at), "dd/MM/yyyy", { locale: dateLocale })} 
                    {language === 'pt' ? ' por ' : ' by '} 
                    {plan.trainer?.first_name} {plan.trainer?.last_name}
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="grid gap-4 md:grid-cols-4 mb-4">
                    <div className="bg-slate-50 dark:bg-gray-900/50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("results.calories")}</p>
                      <p className="text-2xl font-bold text-foreground">{plan.calories} {t("results.kcal")}</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-gray-900/50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("results.protein")}</p>
                      <p className="text-2xl font-bold text-foreground">{plan.protein}g</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-gray-900/50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("results.carbs")}</p>
                      <p className="text-2xl font-bold text-foreground">{plan.carbs}g</p>
                    </div>
                    <div className="bg-slate-50 dark:bg-gray-900/50 p-4 rounded-lg">
                      <p className="text-sm font-medium text-muted-foreground dark:text-gray-400">{t("results.fats")}</p>
                      <p className="text-2xl font-bold text-foreground">{plan.fats}g</p>
                    </div>
                  </div>
                  
                  {plan.food_suggestions && (
                    <div className="mt-4">
                      <h3 className="font-medium mb-2 text-foreground">{t("results.foodSuggestions")}</h3>
                      <div className="bg-slate-50 dark:bg-gray-900/50 p-4 rounded-lg">
                        <p className="whitespace-pre-line text-foreground">{plan.food_suggestions}</p>
                      </div>

                      <div className="mt-4 p-4 bg-slate-50/50 dark:bg-gray-900/50 rounded-lg border border-slate-200/50 dark:border-gray-700/50">
                        <p className="text-sm text-muted-foreground dark:text-gray-400">
                          {t("results.disclaimer")}
                        </p>
                      </div>
                    </div>
                  )}
                  
                  {plan.notes && (
                    <div className="mt-4">
                      <h3 className="font-medium mb-2 text-foreground">{t("form.notes")}</h3>
                      <p className="text-sm text-muted-foreground dark:text-gray-400">{plan.notes}</p>
                    </div>
                  )}
                </CardContent>
              </Card>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white/90 dark:bg-gray-800/90 backdrop-blur-lg rounded-xl shadow-md border border-slate-100/50 dark:border-gray-700/50 overflow-hidden hover:shadow-lg transition-all">
          <Card className="border-0 shadow-none bg-transparent">
            <CardHeader>
              <CardTitle className="text-foreground">{t("title")}</CardTitle>
              <CardDescription className="text-muted-foreground dark:text-gray-400">
                {t("table.noRecommendations")}
              </CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-sm text-muted-foreground dark:text-gray-400">
                {language === 'pt' 
                  ? "Contacte seu personal trainer para adicionar um plano nutricional personalizado." 
                  : "Contact your personal trainer to add a personalized nutrition plan."}
              </p>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
};
